"use client";

import { Bars3Icon, ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useClientConfig } from "@providers/app-provider/client-app-provider";
import { CommonComponent, NavigationItem, SidebarNavigationItem } from "@shared/cms/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@ubud-ui/react-ui-kit";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { HeaderSearchBar } from "./search/header-search-bar";
import { HeaderUserPopup } from "./popup/header-user-popup";

export function Header() {
  const clientConfig = useClientConfig();
  const config = clientConfig?.layoutSetting;

  const pathname = usePathname();

  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [dropdownContext, setDropdownContext] = useState<number | null>(null);
  const handleToggleDropdown = (open: boolean, nav: NavigationItem) => {
    if (open) {
      setDropdownContext(nav.id);
    } else {
      setDropdownContext(null);
    }
  };

  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false);
  const handleToggleSheet = (open: boolean) => {
    setIsSheetOpen(open);
  };

  const getDesktopNavLink = (nav: NavigationItem, i: number) => {
    if (nav.__component === CommonComponent.LinkList) {
      return (
        <Popover
          key={nav.id + "-" + i}
          defaultOpen={dropdownContext === nav.id}
          open={dropdownContext === nav.id}
          onOpenChange={(open: boolean) => handleToggleDropdown(open, nav)}
        >
          <PopoverTrigger asChild>
            <a className="text-gray-700 text-sm font-medium p-0 inline-flex items-center cursor-pointer gap-2 whitespace-nowrap">
              {nav.title}
              {dropdownContext === nav.id ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
            </a>
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              "p-0 w-[100vw] max-h-[80vh] rounded-xl overflow-x-hidden overflow-y-auto md:w-[40vw] max-w-[450px]",
              "px-8 pt-8 pb-0",
            )}
            align="start"
          >
            <h4 className="text-xl font-bold mb-2.5 text-gray-700">{nav.title}</h4>
            {nav.list && nav.list.length > 0 && (
              <div>
                {nav.list.map((item, index) => {
                  return (
                    <Link
                      className="group flex items-center space-x-3 cursor-pointer"
                      key={item.id}
                      href={item.href}
                      target={item.target}
                      onClick={() => handleToggleDropdown(false, nav)}
                    >
                      {item.icon && (
                        <Image
                          className="size-6 min-w-6 object-contain"
                          width={item.icon.width || 24}
                          height={item.icon.height || 24}
                          alt={item.icon.alternativeText || item.icon.name || ""}
                          src={item.icon.url || "/blank-white.webp"}
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 py-6">
                          <div className="flex-1 font-medium text-gray-700 transition-colors group-hover:text-primary">{item.text}</div>
                          <ChevronRightIcon className="size-5 min-w-5" />
                        </div>
                        {index !== nav.list.length - 1 && <hr className="border-gray-100"></hr>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </PopoverContent>
        </Popover>
      );
    }

    return (
      <Link
        key={nav.id}
        href={nav.href}
        aria-current="page"
        className={cn(
          "inline-flex items-center text-sm font-medium whitespace-nowrap",
          nav.href === pathname ? " text-primary" : " text-gray-700",
        )}
      >
        {nav.icon && (
          <Image
            className="size-6 min-w-6 object-contain"
            width={nav.icon.width || 24}
            height={nav.icon.height || 24}
            alt={nav.icon.alternativeText || nav.icon.name || ""}
            src={nav.icon.url || "/blank-white.webp"}
          />
        )}
        <div className="flex-1 w-full">{nav.text}</div>
      </Link>
    );
  };

  const getMobileNavLink = (nav: NavigationItem, i: number) => {
    if (nav.__component === CommonComponent.LinkList) {
      return (
        <Accordion key={nav.title + "-" + i} collapsible type="single" className="w-full">
          <AccordionItem value={nav.title + "-" + i} className="border-b border-gray-100 text-left">
            <AccordionTrigger className="px-4 md:px-6 py-5 text-lg font-medium text-left hover:bg-primary-50 text-gray-900 hover:text-primary hover:opacity-100">
              {nav.title}
            </AccordionTrigger>
            <AccordionContent className={cn("pb-0 text-base")}>
              {nav.list &&
                nav.list.length > 0 &&
                nav.list.map((navItem, index) => {
                  return (
                    <Link
                      key={navItem.id}
                      href={navItem.href}
                      aria-current="page"
                      className={cn(
                        "flex items-center text-sm font-medium whitespace-nowrap px-4 md:px-6 gap-3 hover:bg-primary-50 hover:text-primary",
                        navItem.href === pathname ? " text-primary" : " text-gray-700",
                      )}
                    >
                      {navItem.icon && (
                        <Image
                          className="size-6 min-w-6 object-contain"
                          width={navItem.icon.width || 24}
                          height={navItem.icon.height || 24}
                          alt={navItem.icon.alternativeText || navItem.icon.name || ""}
                          src={navItem.icon.url || "/blank-white.webp"}
                        />
                      )}
                      <div className="flex-1 w-full">
                        <div className="flex items-center gap-4 py-6">
                          <div className="flex-1 font-medium text-base text-gray-700">{navItem.text}</div>
                          <ChevronRightIcon className="size-5 min-w-5" />
                        </div>
                        {index !== nav.list.length - 1 && <hr className="border-gray-100"></hr>}
                      </div>
                    </Link>
                  );
                })}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      );
    }

    return (
      <SheetClose asChild key={nav.href + "-" + i}>
        <Link
          href={nav.href}
          target={nav.target}
          className={cn(
            "block px-4 md:px-6 py-5 text-lg font-medium text-left hover:bg-primary-50 text-gray-900 hover:text-primary border-b border-gray-100",
          )}
        >
          {nav.icon && (
            <Image
              className="size-6 min-w-6 object-contain"
              width={nav.icon.width || 24}
              height={nav.icon.height || 24}
              alt={nav.icon.alternativeText || nav.icon.name || ""}
              src={nav.icon.url || "/blank-white.webp"}
            />
          )}
          <div className="flex-1 w-full">{nav.text}</div>
        </Link>
      </SheetClose>
    );
  };

  const getSidebarNavLink = (nav: SidebarNavigationItem, i: number) => {
    if (nav.__component === CommonComponent.SidebarOtherNavigation) {
      return (
        <div key={nav.id + "-" + i} className="mt-10">
          <div className="pb-2 px-4 md:px-6 text-gray-500 text-sm">{nav.title}</div>
          <div>
            {nav.list &&
              nav.list.length > 0 &&
              nav.list.map((link) => {
                return (
                  <SheetClose asChild key={link.id}>
                    <Link
                      href={link.href}
                      target={link.target}
                      className={cn("flex gap-3 px-4 md:px-6 py-3 text-lg font-medium text-left hover:bg-primary-50 text-gray-900")}
                    >
                      {link.icon && (
                        <Image
                          className="size-5 min-w-5 object-contain"
                          width={link.icon.width || 24}
                          height={link.icon.height || 24}
                          alt={link.icon.alternativeText || link.icon.name || ""}
                          src={link.icon.url || "/blank-white.webp"}
                        />
                      )}
                      <div className="flex-1">
                        <div className="text-sm font-bold">{link.text}</div>
                        {link.description && <div className="text-sm font-normal text-gray-500 mt-1">{link.description}</div>}
                      </div>
                    </Link>
                  </SheetClose>
                );
              })}
          </div>
        </div>
      );
    }

    return (
      <div key={nav.id + "-" + i} className="mt-10">
        {nav.list.map((link) => {
          return (
            <SheetClose asChild key={link.id}>
              <Link
                href={link.href}
                target={link.target}
                className={cn(
                  "flex items-center gap-3 px-4 md:px-6 py-5 text-lg font-medium text-left hover:bg-primary-50 text-gray-900 border-b border-gray-100",
                )}
              >
                {link.icon && (
                  <Image
                    className="size-6 min-w-6 object-contain"
                    width={link.icon.width || 24}
                    height={link.icon.height || 24}
                    alt={link.icon.alternativeText || link.icon.name || ""}
                    src={link.icon.url || "/blank-white.webp"}
                  />
                )}
                <span className="flex-1 font-bold">{link.text}</span>
              </Link>
            </SheetClose>
          );
        })}
      </div>
    );
  };

  return (
    <header>
      <nav className="border-b border-gray-100 bg-white z-50 fixed w-full top-0 left-0">
        <div className="px-4 md:px-6 lg:px-7">
          <div className="flex h-20 justify-between gap-2 md:gap-6">
            {!isSearchOpen && (
              <>
                <div className="flex items-center">
                  <Sheet open={isSheetOpen} onOpenChange={handleToggleSheet}>
                    <SheetTrigger asChild>
                      <a className="cursor-pointer">
                        <span className="sr-only">Open main menu</span>
                        <Bars3Icon aria-hidden="true" className="block size-6 group-data-[open]:hidden" />
                        <XMarkIcon aria-hidden="true" className="hidden size-6 group-data-[open]:block" />
                      </a>
                    </SheetTrigger>
                    <SheetContent className="w-full sm:max-w-full md:max-w-sm overflow-y-auto" closeIconClassName="w-7 h-7" side="left">
                      <SheetHeader>
                        <SheetTitle>
                          {config?.header?.logo && (
                            <div className="flex shrink-0 items-center -ml-2 md:ml-0">
                              <Link href="/">
                                <Image
                                  src={config?.header?.logo?.url || "/blank-white.webp"}
                                  alt={config?.header?.logo?.name}
                                  className="w-32 h-auto inline-block"
                                  width={config?.header?.logo?.width || 128}
                                  height={config?.header?.logo?.height || 32}
                                />
                              </Link>
                            </div>
                          )}
                        </SheetTitle>
                      </SheetHeader>
                      <div className="-mx-6">
                        <div className="block sm:hidden">
                          {config?.headerNavigation?.map((nav, i) => {
                            return getMobileNavLink(nav, i);
                          })}
                        </div>
                        <div>
                          {config?.sidebarNavigation?.map((nav, i) => {
                            return getSidebarNavLink(nav, i);
                          })}
                        </div>
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>
                <div className="flex">
                  <div className="flex shrink-0 items-center">
                    <Link href="/">
                      <Image
                        src={config?.header?.logo?.url || "/blank-white.webp"}
                        alt={config?.header?.logo?.alternativeText || config?.header?.logo?.name || "Karirhub oleh Kemnaker"}
                        className="w-32 h-auto inline-block"
                        width={config?.header?.logo?.width || 128}
                        height={config?.header?.logo?.height || 32}
                      />
                    </Link>
                  </div>
                  <div className="hidden sm:-my-px ml-6 lg:ml-14 sm:flex sm:space-x-6">
                    {config?.headerNavigation.map((nav) => getDesktopNavLink(nav, nav.id))}
                  </div>
                </div>
              </>
            )}
            <div className="flex items-center justify-end gap-4 flex-1">
              <div id="headerSearchBarWrapper" className={cn(pathname === "/" ? "opacity-0" : "", "w-full")}>
                <HeaderSearchBar onSearchOpen={(open) => setIsSearchOpen(open)} />
              </div>
              {config && !isSearchOpen && (
                <div>
                  <HeaderUserPopup />
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
